import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import LivestockActors from "../../component/Workforce/Livestockactor";
import {
  WorkforceDeployment,
  Cooperatives,
} from "../../component/Workforce/WorkTab";
import UnconfiguredSubmodulePlaceholder from "../../component/shared/UnconfiguredSubmodulePlaceholder";
import { useModuleTabs } from "../../config/moduleCatalog";

const LOCAL_TABS = [
  { key: "actors", label: "Livestock Actors" },
  { key: "deployment", label: "Workforce Deployment Records" },
  { key: "coop", label: "Cooperatives / Organisation" },
];

const TAB_COMPONENTS = {
  actors: LivestockActors,
  deployment: WorkforceDeployment,
  coop: Cooperatives,
};

function TabRow({ tabs, activeTab, onTabChange }) {
  return (
    <nav className="mb-0 overflow-x-auto border-b border-gray-200 pb-1">
      <div className="flex min-w-max gap-x-7 gap-y-2">
        {tabs.map(({ key, label }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => onTabChange(key)}
              className={`relative whitespace-nowrap pb-3 text-sm ${
                isActive ? "font-semibold text-green-600" : "font-medium text-gray-700 hover:text-green-700"
              }`}
            >
              {label}
              {isActive && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded bg-green-600" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

const R05_TAB_KEYS = new Set(["actors"]);

export default function WorkforceLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isR05 = user?.roleId === "R05";
  const tabs = useModuleTabs("workforce-institutional", LOCAL_TABS);
  // New (catalog-only) tabs aren't in R05_TAB_KEYS, so R05 never sees them
  // until explicitly granted.
  const visibleTabs = isR05 ? tabs.filter((tab) => R05_TAB_KEYS.has(tab.key)) : tabs;
  const segment = pathname.split("/").filter(Boolean).pop();
  const activeTab = visibleTabs.some((tab) => tab.key === segment) ? segment : visibleTabs[0]?.key;
  const activeTabDef = visibleTabs.find((tab) => tab.key === activeTab);
  const handleTabChange = (key) => navigate(`/workforce-institutional/${key}`);

  const ActiveView = TAB_COMPONENTS[activeTab] ?? LivestockActors;

  return (
    <div className="min-h-screen bg-white px-4 py-5 sm:p-6">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-xl font-bold leading-none text-gray-900">
            Workforce &amp; Institutional Actors
          </h1>
          <p className="mt-1.5 text-xs text-gray-400">
            Workforce registration, deployment, qualifications, and cooperative records
          </p>
        </div>
        <span className="mt-1 text-sm font-semibold text-gray-400">
          FMLD-native
        </span>
      </div>

      <TabRow
        tabs={visibleTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <div className="mt-6">
        {activeTabDef?.isCatalogOnly ? (
          <UnconfiguredSubmodulePlaceholder label={activeTabDef.label} />
        ) : (
          <ActiveView />
        )}
      </div>
    </div>
  );
}

